/**
 * Shared Resident + Reimbursement detail page (Angular `resident-edit-new`).
 *
 * Angular uses ONE detail component for both modules; mode comes from route `pagetype`:
 * - `resident-detail` → Resident Detail
 * - `default-detail` → Reimbursement Detail
 *
 * React: `/resident/:pagetype/...` and `/defaults/:pagetype/...`.
 */
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { useResidentDetailPage } from '../hooks/useResidentDetailPage'
import { ResidentDetailFormContent } from '../components/detail/ResidentDetailFormContent'
import { ResidentDetailDialogs } from '../components/detail/ResidentDetailDialogs'

/** Angular route `pagetype` values. */
export type ResidentDetailPageType = 'resident-detail' | 'default-detail'

export type ResidentDetailPageProps = {
  /** Explicit mode. When omitted, inferred from the URL path. */
  pageType?: ResidentDetailPageType
}

function resolvePageType(
  explicit: ResidentDetailPageType | undefined,
  pathname: string,
  pagetypeParam?: string,
): ResidentDetailPageType {
  if (explicit) return explicit
  if (pagetypeParam === 'default-detail' || pagetypeParam === 'resident-detail') {
    return pagetypeParam
  }
  if (
    pathname.includes('/defaults/') ||
    pathname.includes('/reimbursements/')
  ) {
    return 'default-detail'
  }
  return 'resident-detail'
}

function pageTitle(pageType: ResidentDetailPageType): string {
  // Angular: pageType === 'resident-detail' ? 'Resident Detail' : 'Reimbursement Detail'
  return pageType === 'resident-detail' ? 'RESIDENT DETAIL' : 'REIMBURSEMENT DETAIL'
}

function pageTestIdRoot(pageType: ResidentDetailPageType): string {
  return pageType === 'resident-detail' ? 'resident-detail' : 'reimbursements-detail'
}

/**
 * Heading + breadcrumb match the Application Detail page: the full-width
 * `PageHeader` bar renders flush at the top (no surrounding padding) and page
 * padding lives on the content wrapper below.
 */
export function ResidentDetailPage({ pageType: pageTypeProp }: ResidentDetailPageProps = {}) {
  const { pathname } = useLocation()
  const { pagetype } = useParams<{ pagetype?: string }>()
  const pageType = useMemo(
    () => resolvePageType(pageTypeProp, pathname, pagetype),
    [pageTypeProp, pathname, pagetype],
  )
  const title = pageTitle(pageType)
  const testIdRoot = pageTestIdRoot(pageType)
  const page = useResidentDetailPage()

  if (page.detail.loading) {
    return (
      <div
        className="min-h-screen w-full bg-slate-50/70"
        data-testid={`${testIdRoot}-loading`}
        data-page-type={pageType}
      >
        <PageHeader title={title} testIdRoot={`${testIdRoot}-header`} />
        <div className="px-3 py-4 sm:px-6 md:px-8 sm:py-6">
          <div className="flex justify-center py-20 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
          </div>
        </div>
      </div>
    )
  }

  if (page.detail.error) {
    return (
      <div
        className="min-h-screen w-full bg-slate-50/70"
        data-testid={`${testIdRoot}-error`}
        data-page-type={pageType}
      >
        <PageHeader title={title} testIdRoot={`${testIdRoot}-header`} />
        <div className="px-3 py-4 sm:px-6 md:px-8 sm:py-6">
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {page.detail.error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen w-full bg-slate-50/70"
      data-testid={`${testIdRoot}-page`}
      data-page-type={pageType}
    >
      <PageHeader title={title} testIdRoot={`${testIdRoot}-header`} />
      <div
        className="px-3 py-4 sm:px-6 md:px-8 sm:py-6"
        data-testid={`${testIdRoot}-content`}
      >
        <div className="mx-auto min-w-0 w-full space-y-3 sm:space-y-4">
          <ResidentDetailFormContent ctx={page} />
          <ResidentDetailDialogs ctx={page} />
        </div>
      </div>
    </div>
  )
}
